import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {getDate} from "../../redux/slice/dateSlice";
import {
    getAsseCurrent,
    getAssetByType,
    getAssetDiligence,
    getAssetHistory,
    getAssetLifeCycle,
    getContract,
    getDepreciation,
} from "../../api/static/StaticService";
import {getRequestBulletin, getRequestNotice} from "../../api/static/StaticRequestService";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";
import {
    AssetSummaryCard,
    extractResponseData,
    formatPeriod,
    HistoryCalendarCard,
} from "./AssetDashboardShared";
import {
    AssetBoardListCard,
    AssetDiligenceCard,
    AssetLifeCycleCard,
    AssetTypeTabCard,
} from "../chart/AssetIamDashboardWidgets";

const toListItems = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
};

const normalizeDiligence = (raw) => {
    if (!raw) return null;

    const inner = raw.data;
    const hasDiligencePayload =
        inner &&
        typeof inner === "object" &&
        !Array.isArray(inner) &&
        (Array.isArray(inner.left) || Array.isArray(inner.right));

    if (hasDiligencePayload) {
        const recentName =
            inner.recentName ??
            raw.recentName ??
            raw.title ??
            inner.title ??
            null;

        return {
            ...raw,
            title: recentName ?? raw.title,
            recentName,
            data: {
                ...inner,
                recentName,
            },
        };
    }

    if (inner?.data) {
        const recentName =
            inner.data.recentName ??
            inner.recentName ??
            raw.recentName ??
            raw.title ??
            null;

        return {
            ...raw,
            title: recentName ?? raw.title,
            recentName,
            data: {
                ...inner.data,
                recentName,
            },
        };
    }

    return raw;
};

function DashBoard_Asset_IAM() {
    const currentSearch = useSelector((state) => getDate(state, "1"));
    const contractSearch = useSelector((state) => getDate(state, "3"));
    const diligenceSearch = useSelector((state) => getDate(state, "4"));
    const historySearch = useSelector((state) => getDate(state, "자산관리내역"));

    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        current: null,
        depreciation: null,
        contract: null,
        diligence: null,
        byType: null,
        lifeCycle: null,
        history: null,
        notice: null,
        bulletin: null,
    });

    const fetchDashboard = useCallback(async () => {
        setLoading(true);

        const settled = await Promise.allSettled([
            getAsseCurrent(currentSearch),
            getDepreciation(),
            getContract(contractSearch),
            getAssetDiligence(diligenceSearch),
            getAssetByType(),
            getAssetLifeCycle(diligenceSearch),
            getAssetHistory(historySearch),
            getRequestNotice("main"),
            getRequestBulletin("main"),
        ]);

        setDashboardData({
            current: extractResponseData(settled[0]),
            depreciation: extractResponseData(settled[1]),
            contract: extractResponseData(settled[2]),
            diligence: normalizeDiligence(extractResponseData(settled[3])),
            byType: extractResponseData(settled[4]),
            lifeCycle: extractResponseData(settled[5]),
            history: extractResponseData(settled[6]),
            notice: extractResponseData(settled[7]),
            bulletin: extractResponseData(settled[8]),
        });
        setLoading(false);
    }, [currentSearch, contractSearch, diligenceSearch, historySearch]);

    const reloadHistory = useCallback(async (search) => {
        try {
            const response = await getAssetHistory(search);
            const data = response?.status === 200 ? response.data : null;
            setDashboardData((prev) => ({...prev, history: data}));
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    const noticeItems = useMemo(() => toListItems(dashboardData.notice), [dashboardData.notice]);
    const bulletinItems = useMemo(() => toListItems(dashboardData.bulletin), [dashboardData.bulletin]);

    return (
        <div className="common-container static-asset-dashboard relative flex min-h-0 flex-col">
            {loading && <ChartLoadingOverlay />}

            <div
                className="flex flex-col gap-4 md:grid md:min-h-0 md:flex-1 md:[grid-template-columns:repeat(4,minmax(0,1fr))] md:[grid-template-rows:repeat(3,minmax(0,1fr))]"
            >
                <div className="flex min-h-[220px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <AssetSummaryCard
                        title={dashboardData.current?.title ?? "자산총괄관리"}
                        items={dashboardData.current?.data ?? []}
                        period={formatPeriod(currentSearch)}
                    />
                </div>
                <div className="flex min-h-[220px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <AssetSummaryCard
                        title={dashboardData.depreciation?.title ?? "감가상각/EOS"}
                        items={dashboardData.depreciation?.data ?? []}
                    />
                </div>
                <div className="flex min-h-[220px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <AssetSummaryCard
                        title={dashboardData.contract?.title ?? "자산계약관리"}
                        items={dashboardData.contract?.data ?? []}
                        period={formatPeriod(contractSearch)}
                    />
                </div>
                <div className="flex min-h-[300px] flex-col border rounded-si p-[16px] [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <AssetDiligenceCard
                        data={dashboardData.diligence}
                        loading={loading && !dashboardData.diligence}
                        period={formatPeriod(diligenceSearch)}
                    />
                </div>

                <div className="flex min-h-[340px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:col-span-2 md:block md:min-h-0">
                    <AssetTypeTabCard
                        data={dashboardData.byType}
                        loading={loading && !dashboardData.byType}
                    />
                </div>
                <div className="flex min-h-[320px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:col-span-2 md:block md:min-h-0">
                    <AssetLifeCycleCard
                        data={dashboardData.lifeCycle}
                        loading={loading && !dashboardData.lifeCycle}
                    />
                </div>

                <div className="flex min-h-[380px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:col-span-2 md:block md:min-h-0">
                    <HistoryCalendarCard
                        history={dashboardData.history}
                        onReload={reloadHistory}
                    />
                </div>
                <div className="flex min-h-[280px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <AssetBoardListCard
                        title="공지사항"
                        items={noticeItems}
                        loading={loading && !noticeItems.length}
                        listPath="/settings/noticelist/main"
                        listTitle="공지사항"
                    />
                </div>
                <div className="flex min-h-[280px] flex-col [&>*]:min-h-0 [&>*]:flex-1 md:block md:min-h-0">
                    <AssetBoardListCard
                        title="자료실"
                        items={bulletinItems}
                        loading={loading && !bulletinItems.length}
                        listPath="/settings/bulletinboardlist/main"
                        listTitle="자료실"
                    />
                </div>
            </div>
        </div>
    );
}

export default DashBoard_Asset_IAM;
